import { useEffect, useState } from "react";
import { resolveClientPortalAssetUrl } from "../../../api/clientUploadApi";

export function isResumePdf(resume) {
  if (!resume) return false;
  if (String(resume.mimeType || "").toLowerCase().includes("pdf")) return true;
  return /\.pdf$/i.test(String(resume.fileName || resume.fileUrl || ""));
}

export default function ResumePreview({
  resume,
  title,
  emptyMessage = "No résumé file is attached.",
}) {
  const sourceUrl = resume?.fileUrl ? resolveClientPortalAssetUrl(resume.fileUrl) : "";
  const downloadUrl = sourceUrl
    ? `${sourceUrl}${sourceUrl.includes("?") ? "&" : "?"}download=1`
    : "";
  const pdf = isResumePdf(resume);
  const [preview, setPreview] = useState({ status: "idle", url: "", error: "" });

  useEffect(() => {
    if (!sourceUrl) {
      setPreview({ status: "idle", url: "", error: "" });
      return undefined;
    }
    let cancelled = false;
    let objectUrl = "";
    setPreview({ status: "loading", url: "", error: "" });
    fetch(sourceUrl, { credentials: "include" })
      .then(async (response) => {
        if (!response.ok) {
          const body = await response.json().catch(() => null);
          throw new Error(
            body?.message ||
              "Unable to load this résumé. The file may no longer be on the server."
          );
        }
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setPreview({ status: "ready", url: objectUrl, error: "" });
      })
      .catch((error) => {
        if (!cancelled) {
          setPreview({
            status: "error",
            url: "",
            error: error.message || "Unable to load this résumé.",
          });
        }
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [sourceUrl]);

  if (!sourceUrl) {
    return <p className="empty-state">{emptyMessage}</p>;
  }

  if (preview.status === "loading" || preview.status === "idle") {
    return <p className="empty-state">Loading résumé…</p>;
  }

  if (preview.status === "error") {
    return (
      <div className="mm-admin-card">
        <p className="mm-admin-lead">{preview.error}</p>
      </div>
    );
  }

  return (
    <>
      <p className="muted">{resume?.fileName || "Attached résumé"}</p>
      <div className="mm-admin-actions" style={{ marginBottom: 12 }}>
        <a className="btn btn-secondary" href={sourceUrl} target="_blank" rel="noreferrer">
          Open in new tab
        </a>
        <a className="btn btn-secondary" href={downloadUrl} target="_blank" rel="noreferrer">
          Download
        </a>
      </div>
      {pdf ? (
        <iframe title={title || "Résumé"} src={preview.url} className="mm-admin-modal__frame" />
      ) : (
        <div className="mm-admin-card">
          <p className="mm-admin-lead">
            This résumé isn’t a PDF preview. Open it in a new tab to download or view the original
            file.
          </p>
        </div>
      )}
    </>
  );
}
